import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../../supabase/client';
import InputTimed from '../../components/InputTimed';
import { refreshMatches, setProfile, resetExhausted } from '../../Redux/store';

function MatchSettings() {
  const authUserId = useSelector((s) => s.user.authUserId);
  const profile = useSelector((s) => s.user.profile);
  const dispatch = useDispatch();

  // Load settings from profile preferences only
  const profilePreferences = profile?.preferences || {};
  // Local state for displaying current slider values
  const [distance, setDistance] = useState(profilePreferences.distance || 12000);
  const [lastActive, setLastActive] = useState(profilePreferences.last_active || 30);

  // Reload profile from Supabase after saving preferences
  const reloadProfile = async () => {
    if (authUserId) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUserId)
        .single();
      
      if (data && !error) {
        dispatch(setProfile(data));
      }
    }
  };

  // Save distance preference to Supabase when slider commits
  const handleDistanceChange = async (value) => {
    const newDistance = parseInt(value);
    setDistance(newDistance);
    
    // Update Supabase using RPC to safely merge preferences
    if (authUserId) {
      const { error } = await supabase.rpc('update_user_preferences', {
        p_user_id: authUserId,
        p_updates: { distance: newDistance },
      });
      
      if (error) {
        /* Error handled */
      } else {
        await reloadProfile();
      }
    }
    
    dispatch(refreshMatches());
    dispatch(resetExhausted());
  };

  // Save last_active preference to Supabase when slider commits
  const handleLastActiveChange = async (value) => {
    const newLastActive = parseInt(value);
    setLastActive(newLastActive);
    
    // Update Supabase using RPC to safely merge preferences
    if (authUserId) {
      const { error } = await supabase.rpc('update_user_preferences', {
        p_user_id: authUserId,
        p_updates: { last_active: newLastActive },
      });
      
      if (error) {
        /* Error handled */
      } else {
        await reloadProfile();
      }
    }
    
    dispatch(refreshMatches());
    dispatch(resetExhausted());
  };

  return (
    <div className="space-y-6">
      {/* Distance slider - controls match radius in miles */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wide text-primary">Match Distance</label>
          <span className="text-sm text-muted-foreground">{distance} mi</span>
        </div>
        <InputTimed
          type="range"
          min="5"
          max="12000"
          step="5"
          defaultValue={distance}
          onCommit={handleDistanceChange}
          className="w-full"
          delay={100}
        />
      </section>
      
      {/* Last active slider - controls how recently a user must have been active */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wide text-primary">Last Active</label>
          <span className="text-sm text-muted-foreground">within {lastActive} days</span>
        </div>
        <InputTimed
          type="range"
          min="1"
          max="90"
          step="1"
          defaultValue={lastActive}
          onCommit={handleLastActiveChange}
          className="w-full"
          delay={100}
        />
      </section>
    </div>
  );
}

export default MatchSettings;
